import React, { useState, useEffect, useRef } from "react"
import {
  TeamPerformanceContainer,
  FilterSection,
  KPICardsContainer,
  KPICard,
  TableSection,
  StatusPill,
  TeamPerformanceGlobalStyle,
  OverlayProgress
} from "./TeamPerformanceMetricScreen.styled"
import { useTranslation } from "react-i18next"
import { useTeamPerformanceMetrics } from "./useTeamPerformanceMetrics"
import Paginate from "components/Commons/Pagination/Paginate"
import { Link } from "react-router-dom"

const getInitialDateFrom = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}

const getInitialDateTo = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const TeamPerformanceMetricScreen: React.FC = () => {
  const { t } = useTranslation()
  const [dateFrom, setDateFrom] = useState(getInitialDateFrom())
  const [dateTo, setDateTo] = useState(getInitialDateTo())
  const [selectedOrderType, setSelectedOrderType] = useState("Todos")
  const [page, setPage] = useState(0)
  const tableRef = useRef<HTMLDivElement>(null)

  const {
    metrics,
    pendingOrdersList,
    loading,
    error,
    fetchMetrics,
    maxPage,
    downloadCsv,
    isDownloadingCsv,
    csvProgress,
    pendingOrdersError
  } = useTeamPerformanceMetrics()

  const handleApplyFilters = (pageToFetch = 0) => {
    const fromIso = new Date(`${dateFrom}T00:00:00.000Z`).toISOString()
    const toIso = new Date(`${dateTo}T23:59:59.999Z`).toISOString()
    fetchMetrics(fromIso, toIso, selectedOrderType, pageToFetch)
  }

  const handleClickPage = (pageSelected: number) => {
    setPage(pageSelected)
    handleApplyFilters(pageSelected)
  }

  useEffect(() => {
    // Cuando cambian los filtros principales, volver a la pagina 0 y buscar
    setPage(0)
    handleApplyFilters(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrderType]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <TeamPerformanceContainer>
      <TeamPerformanceGlobalStyle />

      {isDownloadingCsv && (
        <OverlayProgress>
          <div className="progress-box">
            <h3>Generando Reporte CSV</h3>
            <div className="progress-bar-container">
              <div className="progress-bar-fill" style={{ width: `${csvProgress}%` }}></div>
            </div>
            <div className="progress-text">{csvProgress}%</div>
            <p style={{ margin: "10px 0 0 0", fontSize: "12px", color: "#666" }}>Por favor, no cierres esta ventana...</p>
          </div>
        </OverlayProgress>
      )}

      <h1 className="header-title">Dashboard de Performance</h1>

      {error && <div style={{ color: 'white', backgroundColor: '#d32f2f', padding: '10px', borderRadius: '4px', marginBottom: '15px' }}>{error}</div>}

      <FilterSection>
        <div className="filter-group">
          <div className="filter-item">
            <label>Tipo de Orden</label>
            <select value={selectedOrderType} onChange={(e) => setSelectedOrderType(e.target.value)}>
              <option value="Todos">Todos</option>
              <option value="HD">HD</option>
              <option value="SP">SP</option>
            </select>
          </div>

          <div className="filter-item">
            <label>Fecha Desde</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>

          <div className="filter-item">
            <label>Fecha Hasta</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>

          <button className="btn-apply" onClick={() => { setPage(0); handleApplyFilters(0); }} disabled={loading}>
            {loading ? "Cargando..." : "Aplicar Filtros"}
          </button>
        </div>

        <button
          className="btn-download"
          onClick={() => {
            const fromIso = new Date(`${dateFrom}T00:00:00.000Z`).toISOString()
            const toIso = new Date(`${dateTo}T23:59:59.999Z`).toISOString()
            downloadCsv(fromIso, toIso, selectedOrderType)
          }}
          disabled={isDownloadingCsv || metrics.pendingOrders === 0}
        >
          {isDownloadingCsv ? "Generando reporte..." : "Descargar Reporte CSV"}
        </button>
      </FilterSection>

      <KPICardsContainer>
        <KPICard borderColor="#0056b3">
          <span className="kpi-title">Q ÓRDENES INGRESADAS</span>
          <span className="kpi-value default-text">
            {metrics.enteredOrders === null ? "..." : metrics.enteredOrders}
          </span>
          <span className="kpi-subtitle">En el rango seleccionado</span>
        </KPICard>

        <KPICard borderColor="#0056b3">
          <span className="kpi-title">Q ÓRDENES PREPARADAS</span>
          <span className="kpi-value default-text">
            {metrics.preparedOrders === null ? "..." : metrics.preparedOrders}
          </span>
          <span className="kpi-subtitle">En el rango seleccionado</span>
        </KPICard>

        <KPICard borderColor="#d32f2f">
          <span className="kpi-title">Q ÓRDENES PENDIENTES</span>
          <span className="kpi-value red-text">
            {metrics.pendingOrders === null ? "..." : metrics.pendingOrders}
          </span>
          <span className="kpi-subtitle">Actual: En cola o en proceso</span>
        </KPICard>

        <KPICard borderColor="#28a745">
          <span className="kpi-title">CUMPLIMIENTO DE TIENDA</span>
          <span className="kpi-value green-text">
            {metrics.storeCompliancePercentage === null ? "..." : `${metrics.storeCompliancePercentage}%`}
          </span>
        </KPICard>
      </KPICardsContainer>

      <TableSection ref={tableRef}>
        <div className="table-header">
          <div className="table-title">
            Sábana de Datos: Órdenes Pendientes <span className="badge">{metrics.pendingOrders} Órdenes</span>
          </div>
          <div className="table-subtitle">Ordenado por tiempo de espera (las más antiguas primero).</div>
        </div>

        <div className="table-responsive">
          <table>
            <thead>
              <tr>
                <th>Tiempo Sin Preparar ↓</th>
                <th>N° Orden</th>
                <th>Canal</th>
                <th>Tipo Despacho</th>
                <th>Modalidad</th>
                <th>Estado Actual</th>
                <th>Fecha/Hora Ingreso</th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody>
              {pendingOrdersError ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: "center", padding: "20px", color: "red" }}>Ocurrió un error al cargar las órdenes pendientes.</td>
                </tr>
              ) : pendingOrdersList === null ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: "center", padding: "20px" }}>Cargando...</td>
                </tr>
              ) : pendingOrdersList.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: "center", padding: "20px" }}>No hay órdenes pendientes.</td>
                </tr>
              ) : (
                pendingOrdersList.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <StatusPill bgColor={row.timeWaitingBg} textColor={row.timeWaitingColor}>
                        {row.timeWaiting}
                      </StatusPill>
                    </td>
                    <td><strong>{row.orderId}</strong></td>
                    <td>{row.channel}</td>
                    <td style={{ textAlign: "center" }}>{row.shippingType}</td>
                    <td>{t(`${row.modality}`, row.modality)}</td>
                    <td>{t(`${row.currentStatus}.title`, row.currentStatus)}</td>
                    <td>{row.dateStatus}</td>
                    <td>
                      {row.detailUrl ? (
                        <Link to={row.detailUrl}>Ver detalle</Link>
                      ) : (
                        <span>Ver detalle</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {maxPage > 1 && (
          <Paginate
            pageCount={maxPage}
            onChangePage={handleClickPage}
            elementFocus={tableRef}
            pageInit={page}
          />
        )}
      </TableSection>
    </TeamPerformanceContainer>
  )
}

export default TeamPerformanceMetricScreen
